import { environment } from 'src/environments/environment';
import { TMCClientParameters } from 'src/tmc-common/tmc-client-parameters';
import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { TranslationService } from '@sdbn/common';
import { interval, Subscription } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import { ContextChange, WidgetStyle } from '@sdbn/appclient';
import { CustomFilter, CustomFilterType } from 'src/tmc-common/model/custom-filter';
import { AbstractWidgetComponent } from '@sdbn/appclient';
import { VisitIdentity, PersonalVisit } from '../personal-visit-list.model';
import { ComponentSelectionStorageService } from 'src/tmc-common/services/component-selection-storage.service';
import { PersonalVisitListService } from '../personal-visit-list.service';
import { PersonalVisitFilterModel } from '../../personal-visit-filter/personal-visit-filter.model';
import { ConfigurationService } from '@sdbn/appclient';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';

const TABLE_SELECTION_KEY = 'personal-visits.table.selection';

@Component({
  selector: 'app-personal-visit-list',
  templateUrl: './personal-visit-list.component.html',
  styleUrls: ['./personal-visit-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PersonalVisitListComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {
  // controls if an initial load of transport must be performed. Must be executed only once!
  initialLoad = true;

  initialized = false;

  filters: Array<CustomFilter> = null;
  filterModel = new PersonalVisitFilterModel();
  filterSubscription: Subscription;

  // Table parameters
  totalCount = 0;
  dataSource = new MatTableDataSource(new Array<PersonalVisit>());
  dataSourceSubscription: Subscription;
  selection = new SelectionModel<PersonalVisit>();
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  defaultVisibleColumns = ['hint', 'name', 'firstname', 'company', 'visitedPerson', 'visitedPersonName', 'visitedPersonDepartment', 'reason', 'notes',
    'numberPlate', 'start', 'registrationCode', 'role', 'filler', 'expectedArrival', 'lastTerminalRegistrationDate', 'lastClientRegistrationDate', 'invitationSentDate', 'visitStatus', 'instructionsStatus', 'supplementaryEquipments', 'internal'];

  columnsToDisplay: string[] = [];

  sortEmptyValuestoEndForColumns: string[] = ['start', 'expectedArrivalSortValue', ''];
  sortColumn = 'start';
  sortDirection: SortDirection = 'asc' as SortDirection;
  // END Table parameters

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  constructor(
    private visitService: PersonalVisitListService,
    public translationService: TranslationService,
    private componentSelectionStorageService: ComponentSelectionStorageService,
    private configurationService: ConfigurationService,
    private tmcContextService: TmcContextService
  ) {

    super();

    this.setupColumns();

    this.filterSubscription = this.tmcContextService.updated.subscribe(next => {
      if (next != null) {
        this.filters = this.tmcContextService.getYardConfiguration().customFilters.filter(f => f.type === CustomFilterType.VISITOR_LIST && !f.displayOnly);
        this.initializeComponent();
      }
    });


    // the sorting based on ViewChild, needs to be initialized after view init because of *ngIf
    this.dataSource.sortingDataAccessor = (item: PersonalVisit, property) => {
      return item[property];
    };
    this.dataSource.data = [];
    this.dataSource.sortData = this.sortData.bind(this);
    // we need to subscribe to data source to react on changes in visible rows
    this.dataSourceSubscription = this.dataSource.connect().subscribe(
      () => this.onVisibleRowsChanged()
    );

    this.registerRefreshTimer();
  }

  ngOnInit() {

    // must be set in ngOnInit!
    this.dataSource.sort = this.sort;
    this.dataSource.sort.disableClear = true;

    /** The rule how the entered filter string is matched against rows' data values. */
    this.dataSource.filterPredicate = (data: PersonalVisit, filterString: string) => {

      if (!this.checkAdditionalFilters(data)) {
        return false;
      }

      return true;
    };

  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
  }

  private setupColumns() {
    const columnProperty = this.configurationService.getClientParamValue(TMCClientParameters.P_VISITS_PERSONAL_VISIT_COLUMNS, null);
    if (columnProperty != null && String(columnProperty).length > 0) {

      const columProperties = String(columnProperty).split('|');
      for (const p of columProperties) {
        const checkColumn = this.defaultVisibleColumns.indexOf(p) !== -1;
        if (checkColumn) {
          this.columnsToDisplay.push(p);
        }
      }
      if (!columProperties.includes('expectedArrival')) {
        this.columnsToDisplay.push('expectedArrival');
        this.columnsToDisplay.push('filler');
      }
    } else {
      this.columnsToDisplay.push(...this.defaultVisibleColumns);
    }
  }

  private sortData(data: PersonalVisit[], sort: MatSort) {
    if (!sort.active || sort.direction === '') {
      return data;
    }
    const isAsc = this.sort.direction === 'asc';

    const emptyToEnd = this.sortEmptyValuestoEndForColumns != null
      && this.sortEmptyValuestoEndForColumns.indexOf(sort.active) !== -1;

    let dataWithout = [];
    let dataWith = [];
    data.forEach(d => {
      if (sort.active === this.sortColumn && d.expectedArrivalSortValue == null) {
        dataWithout.push(d);
      } else {
        dataWith.push(d);
      }
    });
    dataWith.sort((a: PersonalVisit, b: PersonalVisit) => {

      // dedicated sort value first
      let value1 = a[sort.active + 'SortValue'];
      let value2 = b[sort.active + 'SortValue'];

      // default if no sort value is available
      if (value1 == null || value2 == null) {
        value1 = a[sort.active];
        value2 = b[sort.active];
      }

      if (emptyToEnd) {
        // Treat null or empty values always as greater
        if ((value1 == null || value1 === '') && (value2 == null || value2 === '')) {
          return  0;
        }
        if (value1 == null || value1 === '') {
          return  +1;
        }
        if (value2 == null || value2 === '') {
          return  -1;
        }
      }
      return this.compare(value1, value2, isAsc);
    });

    return [...dataWith, ...dataWithout];
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    let t = 0;
    if ((a == null || a === '') && (b == null || b === '')) {
      t = 0;
    } else if (a == null || a === '') {
      t = -1;
    } else if (b == null || b === '') {
      t = 1;
    } else {
      t = (a < b ? -1 : 1);
    }
    return t * (isAsc ? 1 : -1);
  }

  private checkFulltextFilter(data: PersonalVisit, filterString: string): boolean {
    const normalizedFilterString = filterString == null ? '' : filterString.toLowerCase();

    for (const k of Object.keys(data)) {
      const fieldValue = data[k];
      if (fieldValue != null) {
        const normalizedFieldValue = String(fieldValue).toLowerCase();
        if (normalizedFieldValue.indexOf(normalizedFilterString) > -1) {
          return true;
        }
      }
    }
    return false;
  }

  private checkAdditionalFilters(data: PersonalVisit): boolean {

    if (this.filterModel.visitorTypes == null || this.filterModel.visitorTypes.length === 0) {
      return true;
    }

    for (const scope of this.filterModel.visitorTypes) {
      if (data.visitorType === scope) {
        return true;
      }
    }
    return false;
  }

  private registerRefreshTimer() {

    // TODO: before executing load, check if this page is visible. If we were on transport view, there is no need to load visitors.

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (environment.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => this.loadVisits());
    } else {
      console.log('Autoreload visits disabled');
    }
  }

  public getHintClasses(visit: PersonalVisit): string[] {
    const hints: string[] = [];
    if (visit.hintStyle) {
      for (const s of visit.hintStyle.split(' ')) {
        hints.push(s);
      }
    }
    return hints;
  }

  public getRowStyles(visit: PersonalVisit) {
    const styles: string[] = [];
    if (this.selection.isSelected(visit)) {
      styles.push('selected');
    } else {
      styles.push('deselected');
    }
    if (visit.rowStyle != null) {
      styles.push(visit.rowStyle);
    }
    return styles;
  }

  public toggleSelection(personalVisit: PersonalVisit) {
    this.selection.select(personalVisit);
    this.dashboardContext.setContextValue('selected-personal-visit', this.selection.isEmpty() ? null : personalVisit);
    this.storeTableSelection();
  }

  private storeTableSelection() {
    this.componentSelectionStorageService.saveToSession(TABLE_SELECTION_KEY, this.selection.selected);
  }

  private onVisibleRowsChanged() {
    const autoselectEnabled = this.configurationService.isEnabled(TMCClientParameters.P_VISITS_PERSONAL_VISIT_AUTO_SELECT_SINGLE_SEARCH_RESULT, false);
    if (autoselectEnabled && this.dataSource.filteredData.length === 1) {
      this.selection.select(this.dataSource.filteredData[0]);
    }
  }


  public loadVisits() {

    const identity = new VisitIdentity();
    if (this.filterModel.filterExternalKey != '-1') {
      const selectedFilter = this.filters.find(f => f.externalKey === this.filterModel.filterExternalKey);
      identity.customFilterId = selectedFilter.id;
    }
    if (this.filterModel.filterText != null && this.filterModel.filterText.trim() !== '') {
      identity.query = this.filterModel.filterText;
    }

    this.visitService.getPersonalVisits(identity).subscribe(
      response => {
        this.totalCount = response.totalCount;
        this.dataSource.data = response.visits;
        this.restoreTableSelection();
        this.createTranslatedValuesOnList();
      },
      error => {
        this.dataSource.data = [];
        console.log('Error wihle Loading list of vists!', error);
      });
  }

  private restoreTableSelection() {
    this.selection.clear();
    const storedSelection: PersonalVisit[] = this.componentSelectionStorageService.getFromSession(TABLE_SELECTION_KEY);
    if (storedSelection) {
      const restoredSelection: PersonalVisit[] = [];
      for (const s of storedSelection) {
        for (const row of this.dataSource.data) {
          if (s.id === row.id) {
            restoredSelection.push(row);
          }
        }
      }
      this.selection.select(...restoredSelection);
      this.dashboardContext.setContextValue('selected-personal-visit', this.selection.isEmpty() ? null : restoredSelection[0]);
      this.storeTableSelection();
    }
  }

  onContextChange(contextChange: ContextChange) {
    if (!this.initialized) {
      return;
    }
    if (contextChange.key === 'visitor-selection') {
      this.refreshBySelection();
    }
    if (contextChange.key === 'personal-visit-changed') {
      this.loadVisits();
    }
  }

  onSetElementAndContext() {
    this.initializeComponent();
  }

  private initializeComponent() {
    if (this.filters != null && this.widgetReference != null && this.dashboardContext != null) {

      // Wenn Filter in der Widget Konfiguration gesetzt ist, dann wird dieser Initial genutzt!
      // Im Spätzeren Verlauf der Anwendung kann es möglich sein,
      // dass der Filter durch 'transport-selection' events auf dem Context überschrieben wird.
      // Beispielsweise von der TransportSelectionComponent im Dashboard.
      if (this.widgetReference.data.filter != null) {
        const filterOfWidgetReference = this.filters.find(f => f.externalKey === this.widgetReference.data.filter);

        const transportFilter = new PersonalVisitFilterModel();
        transportFilter.filterExternalKey = filterOfWidgetReference.externalKey;
        transportFilter.sortColumn = filterOfWidgetReference.sortColumn;
        transportFilter.sortDescending = filterOfWidgetReference.sortDescending;
        transportFilter.tableColumns = filterOfWidgetReference.tableColumns;

        this.dashboardContext.setContextValue('visitor-selection', transportFilter);
      }

      this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
      if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
        this.refreshInterval = this.widgetReference.refreshInterval * 1000;
      } else {
        this.refreshInterval = 30 * 1000;
      }
      this.registerRefreshTimer();

      this.refreshBySelection();

      this.initialized = true;
    }
  }

  private refreshBySelection() {

    const model = this.dashboardContext?.context.get('visitor-selection');

    if (model != null && (this.filterModel.filterExternalKey !== model.filterExternalKey) || this.filterModel.filterText !== model.filterText ) {
      this.filterModel = model;
      this.dataSource.filter = JSON.stringify(model);
      this.loadVisits();
    } else { // .. otherwise only the datasource filter is triggered
      this.filterModel = model;
      this.dataSource.filter = JSON.stringify(model);
    }
  }

  createTranslatedValuesOnList() {
    for (const personalVisit of this.dataSource.data) {
      personalVisit.role = this.translationService.translate(personalVisit.roleDictionaryKey);
    }
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
